import { useState, useRef, useEffect } from 'react'
import { useGoogleLogin } from '@react-oauth/google'

const COUNTRY_PREFIXES = [
  { code: 'AR', name: 'Argentina', prefix: '+549', flag: '🇦🇷' },
  { code: 'UY', name: 'Uruguay', prefix: '+598', flag: '🇺🇾' },
  { code: 'CL', name: 'Chile', prefix: '+56', flag: '🇨🇱' },
  { code: 'PY', name: 'Paraguay', prefix: '+595', flag: '🇵🇾' },
  { code: 'BO', name: 'Bolivia', prefix: '+591', flag: '🇧🇴' },
  { code: 'BR', name: 'Brasil', prefix: '+55', flag: '🇧🇷' },
  { code: 'PE', name: 'Perú', prefix: '+51', flag: '🇵🇪' },
  { code: 'CO', name: 'Colombia', prefix: '+57', flag: '🇨🇴' },
  { code: 'EC', name: 'Ecuador', prefix: '+593', flag: '🇪🇨' },
  { code: 'VE', name: 'Venezuela', prefix: '+58', flag: '🇻🇪' },
  { code: 'MX', name: 'México', prefix: '+521', flag: '🇲🇽' },
  { code: 'GT', name: 'Guatemala', prefix: '+502', flag: '🇬🇹' },
  { code: 'SV', name: 'El Salvador', prefix: '+503', flag: '🇸🇻' },
  { code: 'HN', name: 'Honduras', prefix: '+504', flag: '🇭🇳' },
  { code: 'NI', name: 'Nicaragua', prefix: '+505', flag: '🇳🇮' },
  { code: 'CR', name: 'Costa Rica', prefix: '+506', flag: '🇨🇷' },
  { code: 'PA', name: 'Panamá', prefix: '+507', flag: '🇵🇦' },
  { code: 'CU', name: 'Cuba', prefix: '+535', flag: '🇨🇺' },
  { code: 'DO', name: 'Rep. Dominicana', prefix: '+1', flag: '🇩🇴' },
  { code: 'PR', name: 'Puerto Rico', prefix: '+1', flag: '🇵🇷' },
  { code: 'ES', name: 'España', prefix: '+34', flag: '🇪🇸' },
]

const TIMEZONE_TO_COUNTRY = {
  'America/Argentina/Buenos_Aires': 'AR', 'America/Argentina/Cordoba': 'AR',
  'America/Argentina/Salta': 'AR', 'America/Argentina/Tucuman': 'AR',
  'America/Argentina/La_Rioja': 'AR', 'America/Argentina/San_Juan': 'AR',
  'America/Argentina/Jujuy': 'AR', 'America/Argentina/Catamarca': 'AR',
  'America/Argentina/Mendoza': 'AR', 'America/Argentina/San_Luis': 'AR',
  'America/Argentina/Rio_Gallegos': 'AR', 'America/Argentina/Ushuaia': 'AR',
  'America/Buenos_Aires': 'AR',
  'America/Montevideo': 'UY',
  'America/Santiago': 'CL', 'Pacific/Easter': 'CL',
  'America/Asuncion': 'PY',
  'America/La_Paz': 'BO',
  'America/Sao_Paulo': 'BR', 'America/Noronha': 'BR', 'America/Belem': 'BR',
  'America/Fortaleza': 'BR', 'America/Recife': 'BR', 'America/Araguaina': 'BR',
  'America/Maceio': 'BR', 'America/Bahia': 'BR', 'America/Campo_Grande': 'BR',
  'America/Cuiaba': 'BR', 'America/Porto_Velho': 'BR', 'America/Boa_Vista': 'BR',
  'America/Manaus': 'BR', 'America/Eirunepe': 'BR', 'America/Rio_Branco': 'BR',
  'America/Lima': 'PE',
  'America/Bogota': 'CO',
  'America/Guayaquil': 'EC', 'Pacific/Galapagos': 'EC',
  'America/Caracas': 'VE',
  'America/Mexico_City': 'MX', 'America/Cancun': 'MX', 'America/Merida': 'MX',
  'America/Monterrey': 'MX', 'America/Matamoros': 'MX', 'America/Mazatlan': 'MX',
  'America/Chihuahua': 'MX', 'America/Ojinaga': 'MX', 'America/Hermosillo': 'MX',
  'America/Tijuana': 'MX', 'America/Bahia_Banderas': 'MX',
  'America/Guatemala': 'GT',
  'America/El_Salvador': 'SV',
  'America/Tegucigalpa': 'HN',
  'America/Managua': 'NI',
  'America/Costa_Rica': 'CR',
  'America/Panama': 'PA',
  'America/Havana': 'CU',
  'America/Santo_Domingo': 'DO',
  'America/Puerto_Rico': 'PR',
  'Europe/Madrid': 'ES', 'Atlantic/Canary': 'ES',
}

function detectCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const code = TIMEZONE_TO_COUNTRY[tz]
    if (code) {
      const match = COUNTRY_PREFIXES.find((c) => c.code === code)
      if (match) return match
    }
  } catch { /* ignore */ }
  return COUNTRY_PREFIXES[0]
}

const LoginModal = ({ onLogin, onClose }) => {
  const [authMethod, setAuthMethod] = useState(null) // null | 'phone' | 'email' | 'google'
  const [step, setStep] = useState(1) // 1: datos, 2: código
  const [form, setForm] = useState({ nombre: '', telefono: '', email: '' })
  const [selectedCountry, setSelectedCountry] = useState(detectCountry)
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)
  const [code, setCode] = useState('')
  const [fieldError, setFieldError] = useState('')
  const [apiError, setApiError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const nombreInputRef = useRef(null)
  const codeInputRef = useRef(null)
  const dropdownRef = useRef(null)
  const justWentBack = useRef(false)
  const cooldownTimerRef = useRef(null)

  const API_URL = import.meta.env.VITE_API_URL

  useEffect(() => {
    if (authMethod && step === 1 && nombreInputRef.current) {
      nombreInputRef.current.focus()
    }
    if (step === 2 && codeInputRef.current) {
      codeInputRef.current.focus()
    }
  }, [authMethod, step])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current)
    }
  }, [])

  const startResendCooldown = () => {
    setResendCooldown(180)
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current)
    cooldownTimerRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const formatCooldown = (seconds) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const getFullPhoneNumber = () => `${selectedCountry.prefix}${form.telefono}`

  const isValidPhone = (phone) => phone.length >= 8
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  const resetToMethodSelection = () => {
    setAuthMethod(null)
    setStep(1)
    setForm({ nombre: '', telefono: '', email: '' })
    setCode('')
    setFieldError('')
    setApiError('')
  }

  // --- Phone/SMS flow ---
  const handleSubmitPhone = async (e) => {
    e.preventDefault()
    if (justWentBack.current) { justWentBack.current = false; return }
    setFieldError('')
    setApiError('')

    if (!isValidPhone(form.telefono)) {
      setFieldError('Teléfono inválido, corrige y vuelve a intentar')
      return
    }

    if (form.nombre && form.telefono) {
      setIsSubmitting(true)
      try {
        const response = await fetch(`${API_URL}owners?sms_pre_auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.nombre,
            phone: form.telefono,
            country_code: selectedCountry.code,
          }),
        })
        const result = await response.json()
        if (result.status === 'SUCCESS') {
          setStep(2)
          startResendCooldown()
        } else {
          setApiError(result.message || 'Error al enviar el código. Intenta de nuevo.')
        }
      } catch {
        setApiError('Error de conexión. Verifica tu internet e intenta de nuevo.')
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handleResendPhoneCode = async () => {
    setApiError('')
    setIsSubmitting(true)
    try {
      const response = await fetch(`${API_URL}owners?sms_pre_auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.nombre,
          phone: form.telefono,
          country_code: selectedCountry.code,
        }),
      })
      const result = await response.json()
      if (result.status === 'SUCCESS') {
        startResendCooldown()
      } else {
        setApiError(result.message || 'Error al reenviar el código.')
      }
    } catch {
      setApiError('Error de conexión. Verifica tu internet e intenta de nuevo.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleVerifyPhoneCode = async (e) => {
    e.preventDefault()
    setApiError('')
    if (code.length < 4) { setApiError('Ingresa el código completo'); return }

    setIsSubmitting(true)
    try {
      const response = await fetch(`${API_URL}owners?sms_auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          phone: form.telefono,
          country_code: selectedCountry.code,
          validation_code: code,
        }),
      })
      const result = await response.json()
      if (result.status === 'SUCCESS') {
        const userData = {
          ...form,
          telefono: getFullPhoneNumber(),
          countryCode: selectedCountry.code,
        }
        localStorage.setItem('doctorvet_logged_user', JSON.stringify(userData))
        onLogin(userData)
      } else {
        setApiError(result.message || 'Código inválido. Intenta de nuevo.')
      }
    } catch {
      setApiError('Error de conexión. Verifica tu internet e intenta de nuevo.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // --- Email flow ---
  const handleSubmitEmail = async (e) => {
    e.preventDefault()
    if (justWentBack.current) { justWentBack.current = false; return }
    setFieldError('')
    setApiError('')

    if (!isValidEmail(form.email)) {
      setFieldError('Email inválido, corrige y vuelve a intentar')
      return
    }

    if (form.nombre && form.email) {
      setIsSubmitting(true)
      try {
        const response = await fetch(`${API_URL}owners?email_pre_auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: form.nombre,
            email: form.email,
          }),
        })
        const result = await response.json()
        if (result.status === 'SUCCESS') {
          setStep(2)
          startResendCooldown()
        } else {
          setApiError(result.message || 'Error al enviar el código. Intenta de nuevo.')
        }
      } catch {
        setApiError('Error de conexión. Verifica tu internet e intenta de nuevo.')
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handleResendEmailCode = async () => {
    setApiError('')
    setIsSubmitting(true)
    try {
      const response = await fetch(`${API_URL}owners?email_pre_auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.nombre,
          email: form.email,
        }),
      })
      const result = await response.json()
      if (result.status === 'SUCCESS') {
        startResendCooldown()
      } else {
        setApiError(result.message || 'Error al reenviar el código.')
      }
    } catch {
      setApiError('Error de conexión. Verifica tu internet e intenta de nuevo.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleVerifyEmailCode = async (e) => {
    e.preventDefault()
    setApiError('')
    if (code.length < 4) { setApiError('Ingresa el código completo'); return }

    setIsSubmitting(true)
    try {
      const response = await fetch(`${API_URL}owners?email_auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: form.email,
          validation_code: code,
        }),
      })
      const result = await response.json()
      if (result.status === 'SUCCESS') {
        const userData = { ...form }
        localStorage.setItem('doctorvet_logged_user', JSON.stringify(userData))
        onLogin(userData)
      } else {
        setApiError(result.message || 'Código inválido. Intenta de nuevo.')
      }
    } catch {
      setApiError('Error de conexión. Verifica tu internet e intenta de nuevo.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // --- Google flow ---
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setApiError('')
      setIsSubmitting(true)
      try {
        const userInfoResponse = await fetch(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
        )
        const userInfo = await userInfoResponse.json()

        const response = await fetch(`${API_URL}owners?google_auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            google_token: tokenResponse.access_token,
          }),
        })
        const result = await response.json()
        if (result.status === 'SUCCESS') {
          const userData = {
            nombre: userInfo.name,
            email: userInfo.email,
            picture: userInfo.picture,
          }
          localStorage.setItem('doctorvet_logged_user', JSON.stringify(userData))
          onLogin(userData)
        } else {
          setApiError(result.message || 'Error al iniciar sesión con Google.')
        }
      } catch {
        setApiError('Error de conexión. Verifica tu internet e intenta de nuevo.')
      } finally {
        setIsSubmitting(false)
      }
    },
    onError: () => {
      setApiError('Error en el login con Google. Intenta de nuevo.')
    },
    flow: 'implicit',
  })

  const handleGoogleLogin = () => {
    setApiError('')
    googleLogin()
  }

  const handleSelectCountry = (country) => {
    setSelectedCountry(country)
    setShowCountryDropdown(false)
  }

  const getTitle = () => {
    if (!authMethod) return 'Regístrate para continuar'
    if (step === 2) {
      return authMethod === 'phone' ? 'Verifica tu teléfono' : 'Verifica tu email'
    }
    return authMethod === 'phone' ? 'Ingresa con teléfono' : 'Ingresa con email'
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
          {getTitle()}
        </h2>

        {/* --- Method selection --- */}
        {!authMethod && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setAuthMethod('email')}
              className="w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="text-gray-700 font-medium">Continuar con Email</span>
            </button>

            <button
              type="button"
              onClick={() => setAuthMethod('phone')}
              className="w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span className="text-gray-700 font-medium">Continuar con Teléfono</span>
            </button>

            <div className="relative flex items-center my-1">
              <div className="flex-grow border-t border-gray-200"></div>
              <span className="mx-3 text-xs text-gray-400">o</span>
              <div className="flex-grow border-t border-gray-200"></div>
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isSubmitting}
              className="w-full flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              <span className="text-gray-700 font-medium">
                {isSubmitting ? 'Conectando...' : 'Continuar con Google'}
              </span>
            </button>

            {apiError && (
              <p className="text-xs text-red-500 text-center">{apiError}</p>
            )}
          </div>
        )}

        {/* --- Phone: step 1 (name + phone) --- */}
        {authMethod === 'phone' && step === 1 && (
          <form onSubmit={handleSubmitPhone} className="space-y-3">
            <input
              ref={nombreInputRef}
              type="text"
              placeholder="Nombre y apellido"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
              required
            />
            <div className="relative" ref={dropdownRef}>
              <div className="flex">
                <button
                  type="button"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                  className="flex items-center gap-1 px-3 py-2 border border-gray-200 border-r-0 rounded-l-lg bg-gray-50 hover:bg-gray-100 focus:outline-none focus:border-gray-400 min-w-[100px]"
                >
                  <span className="text-lg">{selectedCountry.flag}</span>
                  <span className="text-sm text-gray-700">{selectedCountry.prefix}</span>
                  <svg className="w-4 h-4 text-gray-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <input
                  type="tel"
                  placeholder="Teléfono"
                  value={form.telefono}
                  onChange={(e) => {
                    setForm({ ...form, telefono: e.target.value.replace(/\D/g, '') })
                    setFieldError('')
                  }}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-r-lg focus:outline-none focus:border-gray-400"
                  required
                />
              </div>
              {showCountryDropdown && (
                <div className="absolute top-full left-0 mt-1 w-64 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  {COUNTRY_PREFIXES.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleSelectCountry(country)}
                      className={`w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left ${
                        selectedCountry.code === country.code ? 'bg-gray-50' : ''
                      }`}
                    >
                      <span className="text-lg">{country.flag}</span>
                      <span className="text-sm text-gray-700 flex-1">{country.name}</span>
                      <span className="text-sm text-gray-500">{country.prefix}</span>
                    </button>
                  ))}
                </div>
              )}
              {fieldError ? (
                <p className="text-xs text-red-500 mt-1">{fieldError}</p>
              ) : (
                <p className="text-xs text-gray-400 mt-1">Recibirás un código SMS para verificar tu teléfono</p>
              )}
            </div>
            {apiError && (
              <p className="text-xs text-red-500 text-center">{apiError}</p>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gray-800 text-white py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Enviando...' : 'Enviar código'}
            </button>
            <button
              type="button"
              onClick={resetToMethodSelection}
              className="w-full text-gray-500 text-sm hover:text-gray-700"
            >
              Volver
            </button>
          </form>
        )}

        {/* --- Phone: step 2 (verify code) --- */}
        {authMethod === 'phone' && step === 2 && (
          <form onSubmit={handleVerifyPhoneCode} className="space-y-3">
            <p className="text-sm text-gray-500 text-center mb-2">
              Ingresa el código enviado a {selectedCountry.flag} {getFullPhoneNumber()}
            </p>
            <input
              ref={codeInputRef}
              type="text"
              placeholder="Código"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, ''))
                setApiError('')
              }}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 text-center text-lg tracking-widest"
              maxLength={6}
            />
            {apiError && (
              <p className="text-xs text-red-500 text-center">{apiError}</p>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gray-800 text-white py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Verificando...' : 'Verificar'}
            </button>
            <button
              type="button"
              onClick={handleResendPhoneCode}
              disabled={isSubmitting || resendCooldown > 0}
              className="w-full text-gray-500 text-sm hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendCooldown > 0
                ? `Reenviar código en ${formatCooldown(resendCooldown)}`
                : 'Reenviar código'}
            </button>
            <button
              type="button"
              onClick={() => { justWentBack.current = true; setStep(1); setApiError(''); setCode('') }}
              className="w-full text-gray-500 text-sm hover:text-gray-700"
            >
              Volver
            </button>
          </form>
        )}

        {/* --- Email: step 1 (name + email) --- */}
        {authMethod === 'email' && step === 1 && (
          <form onSubmit={handleSubmitEmail} className="space-y-3">
            <input
              ref={nombreInputRef}
              type="text"
              placeholder="Nombre y apellido"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
              required
            />
            <div>
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => {
                  setForm({ ...form, email: e.target.value })
                  setFieldError('')
                }}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
                required
              />
              {fieldError ? (
                <p className="text-xs text-red-500 mt-1">{fieldError}</p>
              ) : (
                <p className="text-xs text-gray-400 mt-1">Recibirás un código en tu email para verificar tu cuenta</p>
              )}
            </div>
            {apiError && (
              <p className="text-xs text-red-500 text-center">{apiError}</p>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gray-800 text-white py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Enviando...' : 'Enviar código'}
            </button>
            <button
              type="button"
              onClick={resetToMethodSelection}
              className="w-full text-gray-500 text-sm hover:text-gray-700"
            >
              Volver
            </button>
          </form>
        )}

        {/* --- Email: step 2 (verify code) --- */}
        {authMethod === 'email' && step === 2 && (
          <form onSubmit={handleVerifyEmailCode} className="space-y-3">
            <p className="text-sm text-gray-500 text-center mb-2">
              Ingresa el código enviado a {form.email}
            </p>
            <input
              ref={codeInputRef}
              type="text"
              placeholder="Código"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, ''))
                setApiError('')
              }}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 text-center text-lg tracking-widest"
              maxLength={6}
            />
            {apiError && (
              <p className="text-xs text-red-500 text-center">{apiError}</p>
            )}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gray-800 text-white py-2 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Verificando...' : 'Verificar'}
            </button>
            <button
              type="button"
              onClick={handleResendEmailCode}
              disabled={isSubmitting || resendCooldown > 0}
              className="w-full text-gray-500 text-sm hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendCooldown > 0
                ? `Reenviar código en ${formatCooldown(resendCooldown)}`
                : 'Reenviar código'}
            </button>
            <button
              type="button"
              onClick={() => { justWentBack.current = true; setStep(1); setApiError(''); setCode('') }}
              className="w-full text-gray-500 text-sm hover:text-gray-700"
            >
              Volver
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

export default LoginModal
